import { Button } from '@cluey/cluey-components';
import classNames from 'classnames';
import { isEmpty } from 'lodash';
import moment from 'moment';
import { useCallback, useEffect } from 'react';
import { generatePath, useHistory } from 'react-router-dom';

import { Enrolment } from '../../../../api/types/enrolment';
import { ReactComponent as CheckIcon } from '../../../../assets/images/icon-outline-checked.svg';
import { ContainerLayout } from '../../../../common/hub/ContainerLayout';
import { useGetVouchers } from '../../../../hooks/queries/useVoucherQuery';
import { TicketIcon } from '../../../../icons/TicketIcon';
import { formatError } from '../../../../util/errors';
import { PATH_HUB_ADD_ONE_OFF_SESSION } from '../../../../util/pagePath';
import pluralize from '../../../../util/pluralize';
import { ErrorBlock } from '../../../../v2/legacy/components';
import { ShadowContainer } from '../../../ShadowContainer';

interface CancelSuccessMessageProps {
  selectedEnrolment: Enrolment;
  tracking: string;
  cancelRequestedDate: string | null;
  cleanUp?: () => void;
}

const CancelSuccessMessage = ({
  selectedEnrolment,
  tracking,
  cancelRequestedDate,
  cleanUp,
}: CancelSuccessMessageProps) => {
  const history = useHistory();

  const { enrolmentId } = selectedEnrolment;

  const { data: vouchers, isLoading: isVoucherLoading, error: vouchersError } = useGetVouchers('Active');

  useEffect(() => {
    return () => {
      if (typeof cleanUp === 'function') {
        cleanUp();
      }
    };
    // eslint-disable-next-line
  }, []);

  const handleBookSession = useCallback(() => {
    history.push(
      generatePath(PATH_HUB_ADD_ONE_OFF_SESSION, {
        enrolmentId,
      })
    );
  }, [enrolmentId, history]);

  return (
    <ContainerLayout>
      <ShadowContainer>
        <div
          id="cancel-enrolment-success-message"
          className={classNames('flex flex-col items-center justify-center gap-y-4 lg:mx-40', {
            [tracking]: tracking !== null,
          })}
        >
          <div className="flex h-[60px] w-[60px] items-center justify-center rounded-full">
            <CheckIcon className="text-white" width={60} height={60} />
          </div>
          <h1 className="text-center font-display text-2xl font-bold md:text-[32px]">Cancellation request received</h1>
          <p className="mt-5 text-center text-lg">
            We have received your request to cancel your enrolment. You will receive a confirmation email with the
            details shortly.
          </p>
          {vouchersError && <ErrorBlock errorMsg={formatError(vouchersError)} />}
          {!isVoucherLoading && vouchers && !isEmpty(vouchers) && (
            <div className="mt-7 flex w-full flex-col items-center gap-y-5 rounded-3xl bg-blood-orange-1 p-10 shadow-lg">
              <TicketIcon width={60} height={60} className="fill-blood-orange-4" />
              <h2 className="merriweather-important text-center text-[32px] font-bold">
                You have {vouchers.length} unused {pluralize('voucher', vouchers.length)}
              </h2>
              <p className="poppins-important">
                Use your {pluralize('voucher', vouchers.length)} to book{' '}
                {vouchers.length > 1 ? 'sessions' : 'a session'} before{' '}
                <b>{moment(cancelRequestedDate).format('dddd, D MMMM')}</b>
              </p>
              <Button
                className="ctaButton text-transform-none mt-3"
                appearance="solid"
                size="sm"
                onClick={handleBookSession}
              >
                Book a session
              </Button>
            </div>
          )}
        </div>
      </ShadowContainer>
    </ContainerLayout>
  );
};

export default CancelSuccessMessage;
